import {
  Component,
  OnInit,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  SimpleChanges,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { TimelogService } from 'src/app/services/timelog.service';
import { Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';
import { AdminService } from 'src/app/services/admin.service';
import { DataService } from 'src/app/services/data.service';

@Component({
  selector: 'app-time-logs-list',
  templateUrl: './time-logs-list.component.html',
  styleUrls: ['./time-logs-list.component.scss'],
  standalone: false,
})
export class TimeLogsListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId?: string;
  @Input() projectName?: string;
  @Input() taskId?: string;
  @Input() date?: Date;
  @Input() userId?: string | null;
  @Input() showLogMoreButton: boolean = true;
  /** Hide a log's Edit / Delete buttons (Time Logs page, by Role Privilege). On by default. */
  @Input() showEditButton: boolean = true;
  @Input() showDeleteButton: boolean = true;
  /** Shows the timesheet Export button (Project Details sidebar only). Off by default. */
  @Input() showExport: boolean = false;
  @Output() onLogMoreTime = new EventEmitter<void>();
  @Output() onManualLogTime = new EventEmitter<string>(); // Emit projectId for manual log time
  @Output() onEdit = new EventEmitter<any>();
  @Output() onUpdate = new EventEmitter<void>();

  private timerSubscription?: Subscription;
  timeLogs: any[] = [];
  isLoading: boolean = false;
  showManualLogTimeButton: boolean = false;
  viewMode: 'list' | 'card' = 'list';

  toggleView(mode: 'list' | 'card'): void {
    this.viewMode = mode;
  }

  // Filtered totals
  totalLogged: number = 0;
  totalBillable: number = 0;
  totalNonBillable: number = 0;

  // Filter state
  selectedUserIds: string[] = [];
  selectedDateRange: Date[] | null = null;
  projectMembers: any[] = [];
  isLoadingMembers: boolean = false;

  // Filtered data
  filteredTimeLogs: any[] = [];
  allTimeLogs: any[] = [];

  // Date presets
  datePresets = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This Week', value: 'thisWeek' },
    { label: 'Previous Week', value: 'previousWeek' },
    { label: 'Last 7 Days', value: 'last7Days' },
    { label: 'Last 15 Days', value: 'last15Days' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last Month', value: 'lastMonth' },
    { label: 'Last 30 Days', value: 'last30Days' },
  ];

  skeletonRows = Array(5).fill(0);

  // User state for timer validation
  currentUser: any = null;
  isCurrentUserProjectMember: boolean = false;

  constructor(
    private timelogService: TimelogService,
    private adminService: AdminService,
    private dataService: DataService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadCurrentUser();
    this.loadProjectMembers();
    this.loadTimeLogs();
    this.updateButtonVisibility();

    // Subscribe to timelog creation/update/delete events for instant UI updates
    this.timerSubscription = this.timelogService.timeLogCreated$.subscribe(
      (data: any) => {
        if (data) {
          this.handleInPlaceUpdate(data);
        } else {
          this.refreshTimeLogs();
        }
      },
    );
  }

  /**
   * Handles incoming socket data to update the UI without a full re-fetch
   */
  private handleInPlaceUpdate(data: any): void {
    console.log('[TimeLogList] Handling in-place update:', data);
    const log = data.timelog;
    const timelogId = data.timelogId || log?._id || log?.id;

    if (data.timelogId && !log) {
      // Deletion case
      console.log('[TimeLogList] Deleting log from local state:', timelogId);
      this.allTimeLogs = this.allTimeLogs.filter(
        (t) => (t._id || t.id) !== timelogId,
      );
    } else if (log) {
      // Creation or Update case
      const incomingId = log._id || log.id;
      const index = this.allTimeLogs.findIndex(
        (t) => (t._id || t.id) === incomingId,
      );

      if (index !== -1) {
        console.log('[TimeLogList] Updating existing log at index:', index);
        console.log(
          '[TimeLogList] Existing log description:',
          this.allTimeLogs[index].description,
        );
        console.log('[TimeLogList] Incoming log description:', log.description);

        // Deep merge or specific field merge
        this.allTimeLogs[index] = {
          ...this.allTimeLogs[index],
          ...log,
          // Ensure nested objects like projectId are not partially overwritten if log only has ID
          projectId: log.projectId || this.allTimeLogs[index].projectId,
          taskId: log.taskId || this.allTimeLogs[index].taskId,
          userId: log.userId || this.allTimeLogs[index].userId,
          loggedBy: log.loggedBy || this.allTimeLogs[index].loggedBy,
        };

        console.log(
          '[TimeLogList] Updated log description:',
          this.allTimeLogs[index].description,
        );
      } else {
        // Only add if it belongs to this context
        const logProjectId =
          log.projectId?._id || log.projectId?.id || log.projectId;
        const logTaskId = log.taskId?._id || log.taskId?.id || log.taskId;

        const isSameProject = this.projectId === logProjectId;
        const isSameTask = !this.taskId || this.taskId === logTaskId;

        console.log('[TimeLogList] New log check:', {
          isSameProject,
          isSameTask,
          contextProject: this.projectId,
          logProjectId,
        });

        if (isSameProject && isSameTask) {
          // Check for existence again just in case of race conditions
          const exists = this.allTimeLogs.some(
            (t) => (t._id || t.id) === incomingId,
          );
          if (!exists) {
            this.allTimeLogs.unshift(log);
          }
        }
      }
    }

    this.applyFilters();
    this.cdr.detectChanges(); // Force UI update
  }

  /**
   * Public method for parent components to manually update local state
   */
  updateLocalLog(log: any): void {
    if (!log) return;
    if (Array.isArray(log)) {
      log.forEach((l: any) => {
        if (l?._id) this.handleInPlaceUpdate({ timelog: l });
      });
    } else if (log._id) {
      this.handleInPlaceUpdate({ timelog: log });
    }
  }

  ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['projectId'] ||
      changes['taskId'] ||
      changes['date'] ||
      changes['userId']
    ) {
      this.clearFilters();
      if (changes['projectId']) {
        this.loadProjectMembers();
      }
      this.loadTimeLogs();
      this.updateButtonVisibility();
    }
  }

  updateButtonVisibility(): void {
    // Show "Log time" button when only projectId (no taskId) and not in date-view mode
    this.showManualLogTimeButton =
      !!this.projectId && !this.taskId && !this.date;
  }

  loadTimeLogs(): void {
    if (!this.projectId && !this.taskId && (!this.userId || !this.date)) {
      console.warn('No context provided for time logs');
      return;
    }

    this.isLoading = true;

    // If taskId is provided, fetch time logs for that task
    if (this.taskId) {
      this.timelogService.getTimeLogsByTask(this.taskId).subscribe({
        next: (res: any) => {
          this.allTimeLogs = res?.data || [];
          this.applyFilters();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          console.error('Error loading time logs:', err);
          this.timeLogs = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
    }
    // If userId and date are provided (e.g. from chart click)
    else if (this.userId && this.date) {
      const startOfDay = new Date(this.date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(this.date);
      endOfDay.setHours(23, 59, 59, 999);

      this.timelogService
        .getTimeLogsByUser(this.userId, startOfDay, endOfDay)
        .subscribe({
          next: (res: any) => {
            this.allTimeLogs = res?.data || [];
            this.applyFilters();
            this.isLoading = false;
            this.cdr.detectChanges();
          },
          error: (err: any) => {
            console.error('Error loading time logs:', err);
            this.timeLogs = [];
            this.isLoading = false;
            this.cdr.detectChanges();
          },
        });
    }
    // Otherwise, fetch time logs for the entire project
    else if (this.projectId) {
      this.timelogService.getTimeLogsByProject(this.projectId).subscribe({
        next: (res: any) => {
          this.allTimeLogs = res?.data || [];
          this.applyFilters();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          console.error('Error loading time logs:', err);
          this.timeLogs = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
    }
  }

  /**
   * Public method to refresh time logs
   * Can be called from parent components
   */
  refreshTimeLogs(): void {
    this.loadTimeLogs();
  }

  calculateTotals(): void {
    this.totalLogged = 0;
    this.totalBillable = 0;
    this.totalNonBillable = 0;

    this.timeLogs.forEach((log) => {
      const hours = this.convertSecondsToHours(log.duration);
      this.totalLogged += hours;

      if (log.isBillable) {
        this.totalBillable += hours;
      } else {
        this.totalNonBillable += hours;
      }
    });
  }

  convertSecondsToHours(seconds: number): number {
    return seconds / 3600;
  }

  formatDuration(seconds: number): string {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  }

  formatDecimalHours(seconds: number): string {
    const hours = seconds / 3600;
    return hours.toFixed(2);
  }

  formatTime(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  getUserName(log: any): string {
    // const userName =
    //   log.userId?.fullName?.trim() || log.loggedBy?.fullName?.trim();
    // Falls back to first + last name when fullName comes through empty
    // (it did in live updates after logging/editing time).
    const nameOf = (u: any): string =>
      u?.fullName?.trim() ||
      [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
    const userName = nameOf(log.userId) || nameOf(log.loggedBy);
    return userName || 'Unknown User';
  }

  getUserAvatar(log: any): string {
    return (
      log.userId?.profileImg ||
      log.loggedBy?.profileImg ||
      '../assets/images/default-profile.png'
    );
  }

  onLogMoreTimeClick(): void {
    this.onLogMoreTime.emit();
  }

  onManualLogTimeClick(): void {
    this.onManualLogTime.emit(this.projectId || undefined);
  }

  onEditTimeLog(log: any): void {
    // Emit event to parent component to handle edit
    this.onEdit.emit(log);
  }

  showDeleteConfirm = false;
  private pendingDeleteLog: any = null;

  onDeleteTimeLog(log: any): void {
    this.pendingDeleteLog = log;
    this.showDeleteConfirm = true;
  }

  confirmDelete(): void {
    this.showDeleteConfirm = false;
    const log = this.pendingDeleteLog;
    if (!log) return;
    const idToDelete = log._id || log.id;
    this.timelogService.deleteTimeLog(idToDelete).subscribe({
      next: () => {
        this.toastr.success('Time log deleted successfully');
        this.allTimeLogs = this.allTimeLogs.filter(
          (t) => (t._id || t.id) !== idToDelete,
        );
        this.applyFilters();
        this.cdr.detectChanges();
        this.onUpdate.emit();
      },
      error: (err) => {
        console.error('Error deleting time log:', err);
        this.toastr.error(err.error?.message || 'Failed to delete time log');
      },
    });
  }

  // Feature 2: User validation methods
  loadCurrentUser(): void {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        this.currentUser = JSON.parse(userStr);
        this.checkIfUserIsProjectMember();
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
      }
    }
  }

  checkIfUserIsProjectMember(): void {
    if (!this.currentUser || !this.projectId) {
      this.isCurrentUserProjectMember = false;
      return;
    }

    // Use DataService to check membership from cache
    this.isCurrentUserProjectMember =
      this.dataService.isCurrentUserProjectMember(this.projectId);
  }

  get canStartTimer(): boolean {
    return !!(this.currentUser && this.isCurrentUserProjectMember);
  }

  get timerDisabledReason(): string {
    if (!this.currentUser) return 'Please log in to start a timer';
    if (!this.isCurrentUserProjectMember)
      return 'Only project members can start timers';
    return '';
  }

  // Feature 1: Filter methods
  loadProjectMembers(): void {
    if (!this.projectId) return;

    // Check if project members are cached
    if (this.dataService.hasProjectUsers(this.projectId)) {
      const cachedUsers = this.dataService.getProjectUsers(this.projectId);
      if (cachedUsers) {
        this.projectMembers = cachedUsers.map((member: any) => ({
          _id: member.userId?._id || member._id,
          fullName: member.userId?.fullName || member.fullName,
          email: member.userId?.email || member.email,
          profileImg: member.userId?.profileImg || member.profileImg,
        }));
        this.checkIfUserIsProjectMember();
        console.log('Using cached project members for time-logs-list');
        this.cdr.detectChanges();
        return;
      }
    }

    // Load from API and cache
    this.isLoadingMembers = true;
    this.adminService.projectteamMembers(this.projectId).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          // Cache the raw data
          this.dataService.setProjectUsers(this.projectId!, res.data);

          this.projectMembers = res.data.map((member: any) => ({
            _id: member.userId?._id || member._id,
            fullName: member.userId?.fullName || member.fullName,
            email: member.userId?.email || member.email,
            profileImg: member.userId?.profileImg || member.profileImg,
          }));
        }
        this.isLoadingMembers = false;
        this.checkIfUserIsProjectMember();
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('Error loading project members:', error);
        this.isLoadingMembers = false;
        this.cdr.detectChanges();
      },
    });
  }

  getDateRangeFromPreset(preset: string): Date[] {
    const today = new Date();
    const start = new Date();
    const end = new Date();

    switch (preset) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        start.setDate(today.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end.setDate(today.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case 'thisWeek':
        const dayOfWeek = today.getDay();
        start.setDate(today.getDate() - dayOfWeek);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'previousWeek':
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        lastWeekStart.setHours(0, 0, 0, 0);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        lastWeekEnd.setHours(23, 59, 59, 999);
        return [lastWeekStart, lastWeekEnd];
      case 'last7Days':
        start.setDate(today.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'last15Days':
        start.setDate(today.getDate() - 15);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'thisMonth':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(today.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'lastMonth':
        start.setMonth(today.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(today.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'last30Days':
        start.setDate(today.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        return [start, end];
    }

    return [start, end];
  }

  onUserFilterChange(userIds: string[]): void {
    this.selectedUserIds = userIds;
    this.applyFilters();
  }

  onDateRangeChange(): void {
    this.applyFilters();
  }

  onDatePresetSelect(preset: string): void {
    this.selectedDateRange = this.getDateRangeFromPreset(preset);
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.allTimeLogs];

    // Filter by users
    if (this.selectedUserIds.length > 0) {
      filtered = filtered.filter((log) => {
        const userId = log.userId?._id || log.userId || log.id;
        const loggedById = log.loggedBy?._id || log.loggedBy;
        return (
          this.selectedUserIds.includes(userId) ||
          this.selectedUserIds.includes(loggedById)
        );
      });
    }

    // Filter by date range
    if (this.selectedDateRange && this.selectedDateRange.length > 0) {
      const [startDate, endDate] = this.selectedDateRange;
      filtered = filtered.filter((log) => {
        const logDate = new Date(log.startTime || log.date);
        const start = new Date(startDate);
        const end = endDate ? new Date(endDate) : new Date(startDate);

        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        return logDate >= start && logDate <= end;
      });
    }

    this.filteredTimeLogs = filtered;
    this.timeLogs = filtered;
    this.calculateTotals();
    this.cdr.detectChanges();
  }

  clearFilters(): void {
    this.selectedUserIds = [];
    this.selectedDateRange = null;
    this.timeLogs = [...this.allTimeLogs];
    this.filteredTimeLogs = [...this.allTimeLogs];
    this.calculateTotals();
    this.cdr.detectChanges();
  }
}
